<template>
    <div>
        <div v-if="mainButton.info">
            <v-btn color="primary" class="zoezi-button-thin primarytext--text">
                {{$translate('Info')}}
            </v-btn>
        </div>
        <div v-else-if="mainButton.plaintext" class="text--secondary text-center">
            {{mainButton.plaintext}}
        </div>
        <div v-else-if="!mainButton.hasMenu">
            <v-btn :color="mainButton.color" class="zoezi-button-thin primarytext--text" @click.stop="mainButton.action" :loading="mainButton.progress"  :disabled="mainButton.disabled" :block="block">
                {{mainButton.text}}
            </v-btn>
        </div>
        <v-menu v-else-if="mainButton.menuItems && mainButton.menuItems.length" offset-y>
            <template v-slot:activator="{ on }">
                <v-btn :color="mainButton.color" class="zoezi-button-thin primarytext--text" :height="height" :outlined="mainButton.outlined" :loading="mainLoading" v-on="on" :block="block">
                    {{mainButton.text}}
                    <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
            </template>
            <v-list two-line>
                <v-list-item v-for="item in mainButton.menuItems" :key="item.title" @click="item.action">
                    <v-list-item-content>
                        <v-list-item-title v-text="item.title" />
                        <v-list-item-subtitle v-text="item.subtitle" />
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-menu>
        <v-menu v-else offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on }">
                <v-btn :color="mainButton.color" class="zoezi-button-thin primarytext--text" v-on="on" :block="block">
                    {{mainButton.text}} <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
            </template>

            <v-list class="z-no-user-select zoezi-workout-book-button-menu">
                <v-list-item v-for="user in bookableUsers" :key="user.id" :class="user.classes" :disabled="user.disabled"  @click="user.action">
                    <v-avatar size="20" class="mr-1">
                        <img :src="user.image" />
                    </v-avatar>
                    <div class="flex-grow-1">{{user.title}}</div>
                    <v-progress-circular v-if="user.progress" indeterminate color="primary" size="16" width="2" class="ml-2" />
                </v-list-item>
            </v-list>
        </v-menu>
        <div v-if="mainButton.subText">
            <div v-for="sub in mainButton.subText" :class="sub.classes" v-text="sub.text" :key="sub.text" />
        </div>

        <bottom-dialog v-model="buyDialog.show" scrollable max-width="850px" :dialog="!$vuetify.breakpoint.mobile" v-if="buyDialog.show">
            <workout-booking-steps
                :workout="workout"
                :login="buyDialog.login"
                :trial="buyDialog.trial"
                :queue="buyDialog.queue"
                :user_id="buyDialog.user_id"
                @done="gotEvent('done')"
                @error="gotEvent('error')"
                @close="gotEvent('close')"
                @book="gotEvent('book')"
                @canBookForSeveral="gotEvent('canBookForSeveral')"
            />
        </bottom-dialog>

        <workout-dialog v-model="showWorkoutDialog" :workout="workout" :showNumBooked="showNumBooked" v-if="showWorkoutDialog" />
    </div>
</template>

<script>
import WorkoutBookingSteps from '../views/WorkoutBookingSteps.vue'
import BottomDialog from './BottomDialog.vue'
import * as Sentry from '@sentry/browser'

export default {
    name: 'zoezi-workout-book-button',

    props: {
        workout: {
            type: Object
        },
        showExtraInfo: {
            type: Boolean,
            default: false
        },
        showNumBooked: {
            type: Boolean,
            default: true
        },
        block: {
            type: Boolean,
            default: false
        },
        infoOnUnbookable: {
            type: Boolean,
            default: false
        }
    },

    components: {
        WorkoutBookingSteps,
        BottomDialog,
        WorkoutDialog: () => import('./WorkoutDialog.vue')
    },

    data: () =>  ({
        loading: {
            cancel: {},
            book: {},
            queue: {}
        },
        buyDialog: {
            show: false,
            login: false,
            trial: false,
            queue: false
        },
        height: 36,
        showWorkoutDialog: false
    }),

    watch: {
        '$store.state.bookings'(bookings) {
            if (bookings) {
                let wbookings = bookings?.workouts?.filter(w => w.id == this.workout?.id);
                wbookings?.forEach(wb => {
                    if (this.loading.book[wb.user_id] || this.loading.queue[wb.user_id]) {
                        this.setLoading('book', wb.user_id, false);
                    }
                });

                Object.keys(this.loading.cancel).forEach(user_id => {
                    if (!wbookings.find(wb => wb.user_id == user_id)) {
                        this.setLoading('cancel', user_id, false);
                    }
                })
            }
        }
    },

    computed: {
        allBooked() {
            return this.$store.state.user?.canBookFor.every(user => this.workout.isBooked(user));
        },
        someCanQueue() {
            return this.$store.state.user?.canBookFor.some(user => this.workout.getState(user) == 'queue');
        },
        someCanBook() {
            return this.$store.state.user?.canBookFor.some(user => this.workout.getState(user) == 'book');
        },
        someCanBuy() {
            return this.$store.state.user?.canBookFor.some(user => ['bookbuy', 'queuebuy'].includes(this.workout.getState(user)));
        },
        canOnlyBookForSelf() {
            return this.$store.state.user?.canBookFor.length == 1 && this.$store.state.user.canBookFor[0].id == this.$store.state.user.id;
        },
        self() {
            return this.bookableUsers.find(x => x.id == this.$store.state.user.id);
        },
        workoutState() {
            return this.workout.getState();
        },
        membershipActive() {
            return this.$store.state.cards.some(c =>
                c.membership &&
                this.workout.startTime.withinRange(c.validFromDate, c.validToDate)
            );
        },
        bookableUsers() {
            if (!this.$store.state.user) {
                return [];
            }

            let workout = this.workout;

            return this.$store.state.user.canBookFor.map(u => {
                let me = this.$store.state.user.id == u.id;
                let firstname =  me ? this.$translate('me') : u.name.split(' ')[0];
                let firstname2 = me ? this.$translate('You') : firstname;
                let action = null;
                let title, booked, disabled;
                let cancel = () => this.cancel(u.id);
                let book = () => this.book(u.id);
                let bookbuy = () => this.openBuyDialog(null, u.id);
                let progress = this.loading.cancel[u.id] || this.loading.book[u.id] || this.loading.queue[u.id];
                let queued = workout.isInQueue(u);

                let state = this.getState(u);
                switch(state) {
                    case 'cancel':
                        title = `${this.$translate('CANCEL_BOOKING')} ${firstname}`;
                        action = cancel;
                        booked = true;
                        break;
                    case 'queue':
                        title = this.$translate('Put {0} in queue').format(firstname);
                        action = book;
                        break;
                    case 'leavequeue':
                        title = this.$translate('Leave queue for {0}').format(firstname);
                        action = cancel;
                        break;
                    case 'book':
                        title = this.$translate('Book') + ' ' + firstname;
                        action = book;
                        break;
                    case 'bookbuy':
                    case 'queuebuy':
                        title = this.$translate('Buy and book for') + ' ' + firstname;
                        action = bookbuy;
                        break;
                    case 'full':
                        title = firstname + ` - ${this.$translate('fully booked')}`;
                        disabled = true;
                        break;
                    case 'tooearly':
                    case 'toolate':
                    case 'cancelled':
                        disabled = true;
                        break;
                    case 'nobook':
                        title = `${firstname2} ${this.$translate('can not book')}`;
                        if (workout.isBlocked(u)) {
                            let toDate = workout.getBlock(u).toDate;
                            if (toDate) {
                                title = this.$translate("{0} is blocked until {1}").format(firstname2, Date.formatFullShort(toDate));
                            } else {
                                title = this.$translate("{0} is blocked until further notice").format(firstname2);
                            }
                        } else if (workout.hasReachedBookingsLimit(u) || workout.hasReachedIntervalBookingsLimit(u) || workout.hasReachedTrainingCardsBookingsLimit(u)) {
                            title = this.$translate('{0} has reached {1} booking limit').format(firstname2, this.$translate(me ? 'your' : 'their'));
                        }
                        disabled = true;
                        break;
                    default:
                        console.error(state)
                }

                if (progress) {
                    if (this.loading.cancel[u.id]) {
                        title = this.$translate('Removing booking') + ' ' + firstname;
                    } else if (this.loading.queue[u.id]) {
                        title = this.$translate('Places {0} in queue').format(firstname)
                    } else if (this.loading.book[u.id]) {
                        title = this.$translate('BOOKING') + ' ' + firstname;
                    }
                }

                return {
                    id: u.id,
                    image: this.$api.getImageUrl(u.imagekey || '/assets/img/profile_avatar_man.jpg', 200),
                    title,
                    classes: {booked, queued},
                    progress,
                    action,
                    disabled,
                    state
                }
            }).filter(u => !['cancel', 'leavequeue'].includes(u.state))
        },
        mainButton() {
            let hasMenu = !this.canOnlyBookForSelf;
            let workout = this.workout;
            let action = this.canOnlyBookForSelf ? () => this.book(this.$store.state.user.id) : null;

            let progress = this.canOnlyBookForSelf && this.mainLoading;

            let subText;
            let free = this.$translate('FREE_SPOTS');
            if (this.showNumBooked && workout.space && !workout.isFull()) {
                if(!workout.firstCome){
                    subText = [{text: `${workout.space - workout.numBooked} / ${workout.space} ${free}`, classes: 'text-center'}];
                }
                else{
                    subText = [{text: `${workout.space + workout.numQueue - workout.numBooked} / ${workout.space} ${free}`, classes: 'text-center'}];
                }
            }
            if (workout.isCancelled()) {
                return {text: this.$translate('Cancelled'), color: 'primary', disabled: true}
            } else if (this.allBooked) {
                return {info: true, subText};
            } else if (workout.tooEarlyToBook()) {
                return {plaintext: this.$translate('Booking opens') + ' ' + Date.formatTimeRelative(workout.bookableFrom()), disabled: true}
            } else if (workout.tooLateToBook() || !workout.bookableForCustomer) {
                if (this.showExtraInfo) {
                    subText = (workout.bookableForCustomer ? [{text: `${this.$translate('Workouts close for booking')} ` + workout.getCategory().registerHoursBefore + ' ' + this.$translate('hours before start')}] : [{text: `${this.$translate('Workout does not allow booking')}`}]);
                }
                if (!this.infoOnUnbookable) {
                    return {text: this.$translate('Booking closed'), disabled: true, subText};
                }
            } else if (this.someCanQueue) {
                return {text: this.$translate('Queue up'), color: 'warning', hasMenu, action, progress}
            } else if (this.someCanBook) {
                return {text: this.$translate('Book'), color: 'primary', hasMenu, action, progress, subText}
            } else if (this.canOnlyBookForSelf && this.workoutState == 'bookbuy') {
                let menuItems = [];
                menuItems.push(
                    {title: this.$translate('Get started!'), subtitle: this.$translate('Buy training card and book'), action: () => this.openBuyDialog() }
                );

                if (workout.getTrialCards().length > 0) {
                    menuItems.push(
                        {title: this.$translate('Try!'), subtitle: this.$translate('So you get an idea what it’s about!'), action: () => this.openBuyDialog('trial') }
                    )
                }
                return {text: this.$translate('Book'), color: 'primary', menuItems, subText, hasMenu: true}
            } else if (this.canOnlyBookForSelf && this.workoutState == 'queuebuy') {
                let menuItems = [];
                menuItems.push(
                    {title: this.$translate('Get started!'), subtitle: this.$translate('Buy training card and book'), action: () => this.openBuyDialog() }
                );

                if (workout.getTrialCards().length > 0) {
                    menuItems.push(
                        {title: this.$translate('Try!'), subtitle: this.$translate('So you get an idea what it’s about!'), action: () => this.openBuyDialog('trial') }
                    )
                }
                return {text: this.$translate('Queue up'), color: 'warning', menuItems, subText, hasMenu: true}
            } else if (this.canOnlyBookForSelf && workout.isBlocked()) {
                let block = workout.getBlock()
                let text;
                if (block.toDate) {
                    let until;
                    if (Date.newFull(block.toDate).getFullYear() != this.$store.state.now30s.getFullYear()) {
                        until = Date.newFull(block.toDate).yyyymmdd();
                    } else {
                        until = Date.formatFullShort(block.toDate);
                    }
                    text = this.$translate("You are blocked until {0}").format(until)
                } else {
                    text = this.$translate("You are blocked from group workouts until furhter notice");
                }
                let subText = [{text, classes: 'text-center error--text'}];
                return {text: this.$translate('Blocked') , disabled: true, subText}
            } else if (this.canOnlyBookForSelf && (workout.hasReachedBookingsLimit() || workout.hasReachedIntervalBookingsLimit() || workout.hasReachedTrainingCardsBookingsLimit()) ) {
                let reasons = workout.getNoBookReasons();
                let subText = [];
                reasons.forEach(r => {
                    const blr = this.$translate('Booking limit reached') + ',';
                    if (r.startsWith(blr)) { // oh what a hack
                        r = r.substring(23)
                        subText.push({text: r, classes: 'text-center text--secondary'})
                    }
                })
                return {text: this.$translate('Booking limit'), disabled: true, subText}
            } else if (this.someCanBuy) {
                return {text: this.$translate('Book'), color: 'primary', hasMenu, action, progress, subText}
            } else if ((this.workoutState == 'bookbuy' || this.workoutState == 'nobook') && !this.$store.state.isLoggedIn) {
                let menuItems = [];
                menuItems.push(
                    {title: this.$translate('Already a member?'), subtitle: this.$translate('Login and book here'), action: () => this.openBuyDialog('login') }
                );

                menuItems.push(
                    {title: this.$translate('Get started!'), subtitle: this.$translate('Buy training card and book'), action: () => this.openBuyDialog() }
                );

                if (workout.getTrialCards().length > 0) {
                    menuItems.push(
                        {title: this.$translate('Try!'), subtitle: this.$translate('So you get an idea what it’s about!'), action: () => this.openBuyDialog('trial') }
                    )
                }
                return {text: this.$translate('Book'), color: 'primary', menuItems, subText, hasMenu: true}
            }

            return {info: true, subText};
        },
        mainLoading() {
            let id = this.$store.state.user?.id;
            return id && (this.loading.cancel[id] || this.loading.book[id] || this.loading.queue[id]);
        }
    },

    methods: {
        cancel(user_id) {
            this.setLoading('cancel', user_id, true);
            this.$api.post('/api/memberapi/workoutBooking/remove', null, {workout: this.workout.id, booked_user: user_id}).then((response) => {
                this.showWorkoutDialog = false;
                if (!response.ok) {
                    this.setLoading('cancel', user_id, false);
                    this.$store.commit('showErrorDialog', {title: this.$translate('Cancellation error'), text: this.$translate('The booking could not be cancelled')});
                }
            })
        },
        bookFromQueue(user_id) {
            this.setLoading('book', user_id, true);
            this.$api.post('/api/memberapi/workoutBooking/join', null, {workout: this.workout.id}).then((response) => {
                if (response.ok) {
                    this.$emit('booked')
                } else {
                    this.setLoading('book', user_id, false);
                    this.$store.commit('showErrorDialog', {title: this.$translate('Booking error'), text: this.$translate('The booking could not be completed')})
                }
            });
        },
        book(user_id) {
            if (user_id == this.$store.state.user.id)  {
                // FIXME: Here ?
                if (!this.workout.hasValidTrainingCard()) {
                    this.buyDialog.show = true;
                    return;
                }
            }

            let user = this.getUser(user_id);
            if (!this.workout.isFull() && this.workout.bookable(user) && this.workout.isInQueue(user)) {
                this.bookFromQueue(user_id);
                return;
            }

            this.setLoading(this.workout.isFull() ? 'queue' : 'book', user_id, true);

            this.$api.post('/api/memberapi/workoutBooking/add', null, {workout: this.workout.id, method: 'trainingcard', user_id}).then((response) => {
                if (response.ok) {
                    try {
                        let ecommerceTrackingItems = [
                            {
                                id: 'w' + this.workout.id,
                                name: this.workout.workoutType.name,
                                price: this.workout.workoutType.price,
                                count: 1,
                                vat: this.workout.workoutType.vat,
                                currency: 'SEK',
                                discount: null
                            }
                        ];

                        let bundle_id = Date.now();
                        this.$ecommercetracking.addToCart(ecommerceTrackingItems[0]);
                        this.$ecommercetracking.purchase(bundle_id, ecommerceTrackingItems);
                    } catch (error) {
                        Sentry.captureException(error);
                    }

                    this.$emit('booked')
                } else {
                    this.setLoading('book', user_id, false);
                    response.json().then(x => {
                        if (x.type == 'duplicate') {
                            let user = user_id == this.$store.state.user.id ? this.$store.state.user : this.$store.state.user.canBookFor.find(x => x.id == user_id);

                            let text;
                            if (user_id  == this.$store.state.user.id) {
                                let t = this.workout.isInQueue(user) ? 'You are already in queue' : 'You are already booked';
                                text = this.$translate(t)
                            } else {
                                let t = this.workout.isInQueue(user) ? '{0} is already in queue' : '{0} is already booked';
                                text = this.$translate(t).format(user.name.split(' ')[0]);
                            }

                            this.$store.commit('showSnackBar', {text});
                        } else {
                            let info = 'The booking could not be completed';
                            if (!this.membershipActive) {
                                info = 'Your training card requires an active membership';
                            }
                            this.$store.commit('showErrorDialog', {title: this.$translate('Booking error'), text: this.$translate(info)})
                        }
                    })
                }
            })
        },
        getUser(user_id) {
            return user_id == this.$store.state.user.id ? this.$store.state.user : this.$store.state.user.canBookFor.find(x => x.id == user_id);
        },
        gotEvent(what) {
            if (what == 'book') {
                if (this.$store.state.user.canBookFor.length == 1 && this.workoutState != 'nobook') {
                    this.book(this.$store.state.user.id);
                } else {
                    this.showWorkoutDialog = true;
                }
            } else if (what == 'canBookForSeveral') {
                this.buyDialog.show = false;
                this.showWorkoutDialog = true;
            } else{
                this.buyDialog.show = false;
            }
        },
        setLoading(type, key, value) {
            let o = this.loading[type];
            o[key] = value;
            this.loading[type] = Object.assign({}, o);
            if (type == 'book' && !value) {
                this.setLoading('queue', key, false);
            }
        },
        getState(user) {
            return this.workout.getState(user);
        },
        openBuyDialog(type, user_id) {
            this.buyDialog.queue = this.workout.isFull();
            this.buyDialog.trial = (type == 'trial')
            this.buyDialog.login = (type == 'login')
            this.buyDialog.show = true;
            this.buyDialog.user_id = user_id;
        },
    }
}
</script>
